/**
 * Service de gestion des contrats de mandat
 * Génération, envoi par email et gestion des signatures pour les bénéficiaires
 */

import { collection, doc, getDoc, getDocs, query, where, addDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import html2pdf from 'html2pdf.js';
import {
  saveBeneficiaryDocument,
  updateDocumentStatus,
  addSignature,
  getBeneficiaryDocuments
} from './beneficiaryDocumentService';

/**
 * Génère le HTML du contrat de mandat à partir du template et des données du bénéficiaire
 */
export async function genererHTMLMandat(db, beneficiaryId) {
  try {
    // Récupérer les données du bénéficiaire
    const beneficiaryRef = doc(db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);

    if (!beneficiarySnap.exists()) {
      throw new Error('Bénéficiaire non trouvé');
    }

    const beneficiary = beneficiarySnap.data();

    // Récupérer les informations de l'entreprise
    const companyRef = doc(db, 'settings', 'general');
    const companySnap = await getDoc(companyRef);
    const companyInfo = companySnap.exists() ? companySnap.data() : {};

    // Charger le template HTML
    const response = await fetch('/documents/Contrat-Mandat_2025-10-05.html');
    let htmlTemplate = await response.text();

    // Remplacer les champs dynamiques dans le template
    const today = new Date();
    const dateEffet = today.toLocaleDateString('fr-FR');
    const dateDebut = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR'); // +7 jours

    // Préparer les données de remplacement
    const replacements = {
      // Informations bénéficiaire
      '_______________________________________________': beneficiary.firstName && beneficiary.lastName
        ? `${beneficiary.firstName} ${beneficiary.lastName}`
        : '',
      // Email, téléphone, adresse
      'Email :</strong> _______________________________________________': `Email :</strong> ${beneficiary.email || ''}`,
      'Téléphone :</strong> _______________________________________________': `Téléphone :</strong> ${beneficiary.phone || beneficiary.phoneNumber || ''}`,
      'Adresse :</strong> _______________________________________________': `Adresse :</strong> ${beneficiary.address || ''}`,

      // Dates
      '___ / ___ / _____': dateEffet,
      'Date d\'effet :</strong> ___ / ___ / _____': `Date d'effet :</strong> ${dateEffet}`,
      'Date de début des interventions prévue :</strong> ___ / ___ / _____': `Date de début des interventions prévue :</strong> ${dateDebut}`,
    };

    // Appliquer les remplacements basiques
    Object.keys(replacements).forEach(key => {
      const regex = new RegExp(key, 'g');
      htmlTemplate = htmlTemplate.replace(regex, replacements[key]);
    });

    // Remplir les informations spécifiques si disponibles
    if (beneficiary.dateNaissance) {
      const dateNaissance = new Date(beneficiary.dateNaissance.seconds * 1000).toLocaleDateString('fr-FR');
      htmlTemplate = htmlTemplate.replace(
        'Date de naissance :</strong> ___ / ___ / _____',
        `Date de naissance :</strong> ${dateNaissance}`
      );
    }

    // Ajouter les informations du contrat si disponibles dans le profil
    if (beneficiary.contratParams) {
      const params = beneficiary.contratParams;

      // Volume horaire
      if (params.volumeHebdo) {
        htmlTemplate = htmlTemplate.replace(
          'Volume horaire hebdomadaire prévisionnel :</strong> _____ heures',
          `Volume horaire hebdomadaire prévisionnel :</strong> ${params.volumeHebdo} heures`
        );
      }

      // Tarif applicable
      if (params.tarifHoraire) {
        htmlTemplate = htmlTemplate.replace(
          'Tarif horaire de base :</strong> 24,41 €',
          `Tarif horaire de base :</strong> ${params.tarifHoraire} €`
        );
      }
    }

    return htmlTemplate;
  } catch (error) {
    console.error('Erreur lors de la génération du HTML du mandat:', error);
    throw error;
  }
}

/**
 * Génère le PDF du contrat de mandat
 */
export async function genererPDFMandat(db, beneficiaryId) {
  try {
    const htmlContent = await genererHTMLMandat(db, beneficiaryId);

    // Créer un élément temporaire
    const element = document.createElement('div');
    element.innerHTML = htmlContent;
    element.style.width = '210mm'; // A4 width

    // Options de configuration du PDF
    const pdfOptions = {
      margin: [10, 10, 10, 10],
      filename: `Contrat_Mandat_${beneficiaryId}.pdf`,
      image: { type: 'jpeg', quality: 1.0 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    // Générer le PDF
    const pdfBlob = await html2pdf()
      .set(pdfOptions)
      .from(element)
      .output('blob');

    return pdfBlob;
  } catch (error) {
    console.error('Erreur lors de la génération du PDF du mandat:', error);
    throw error;
  }
}

/**
 * Vérifie si un contrat de mandat existe déjà pour ce bénéficiaire
 */
export async function verifierMandatExistant(db, beneficiaryId) {
  try {
    const documents = await getBeneficiaryDocuments(db, beneficiaryId);

    // Chercher un document de type MANDAT non draft
    const mandatExistant = documents.find(
      doc => doc.documentType === 'MANDAT' && doc.status !== 'draft'
    );

    return {
      existe: !!mandatExistant,
      mandat: mandatExistant || null
    };
  } catch (error) {
    console.error('Erreur lors de la vérification du mandat:', error);
    return { existe: false, mandat: null };
  }
}

/**
 * Crée un document de contrat de mandat pour un bénéficiaire
 */
export async function creerContratMandat(db, beneficiaryId, userId) {
  try {
    // Vérifier si un mandat existe déjà
    const { existe, mandat } = await verifierMandatExistant(db, beneficiaryId);

    if (existe) {
      throw new Error('Un contrat de mandat existe déjà pour ce bénéficiaire');
    }

    // Récupérer les données du bénéficiaire
    const beneficiaryRef = doc(db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);

    if (!beneficiarySnap.exists()) {
      throw new Error('Bénéficiaire non trouvé');
    }

    const beneficiary = beneficiarySnap.data();

    // Générer le contenu HTML
    const htmlContent = await genererHTMLMandat(db, beneficiaryId);

    // Préparer les données du document
    const documentData = {
      documentType: 'MANDAT',
      documentName: `Contrat de Mandat - ${beneficiary.firstName} ${beneficiary.lastName}`,
      content: htmlContent,
      status: 'draft',
      createdAt: Timestamp.now(),
      createdBy: userId,
      beneficiaryData: {
        id: beneficiaryId,
        firstName: beneficiary.firstName,
        lastName: beneficiary.lastName,
        email: beneficiary.email,
        phone: beneficiary.phone || beneficiary.phoneNumber,
        address: beneficiary.address
      },
      metadata: {
        version: '2025-10-05',
        generatedAt: new Date().toISOString()
      }
    };

    // Sauvegarder dans Firestore
    const docId = await saveBeneficiaryDocument(db, beneficiaryId, documentData);

    return {
      success: true,
      documentId: docId,
      message: 'Contrat de mandat créé avec succès'
    };
  } catch (error) {
    console.error('Erreur lors de la création du contrat de mandat:', error);
    throw error;
  }
}

/**
 * Envoie le contrat de mandat par email au bénéficiaire
 */
export async function envoyerContratMandat(db, beneficiaryId, documentId, auth) {
  try {
    // Récupérer le document
    const docRef = doc(db, 'clients', beneficiaryId, 'documents', documentId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Document non trouvé');
    }

    const documentData = docSnap.data();

    if (documentData.documentType !== 'MANDAT') {
      throw new Error('Ce document n\'est pas un contrat de mandat');
    }

    // Récupérer les données du bénéficiaire
    const beneficiaryRef = doc(db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);

    if (!beneficiarySnap.exists()) {
      throw new Error('Bénéficiaire non trouvé');
    }

    const beneficiary = beneficiarySnap.data();

    if (!beneficiary.email) {
      throw new Error('Le bénéficiaire n\'a pas d\'adresse email');
    }

    // Générer le PDF
    const pdfBlob = await genererPDFMandat(db, beneficiaryId);

    // Convertir le Blob en base64
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(pdfBlob);
    const pdfBase64 = await base64Promise;

    // Préparer les données pour la Cloud Function
    const emailData = {
      to: beneficiary.email,
      subject: 'Contrat de Mandat OMAD Services',
      type: 'contrat-mandat',
      beneficiaryName: `${beneficiary.firstName} ${beneficiary.lastName}`,
      attachment: {
        filename: `Contrat_Mandat_${beneficiary.lastName}.pdf`,
        content: pdfBase64,
        contentType: 'application/pdf'
      }
    };

    // Appeler la Cloud Function
    const functions = getFunctions();
    const sendEmail = httpsCallable(functions, 'sendMandateEmail');
    const result = await sendEmail(emailData);

    // Mettre à jour le statut du document
    await updateDoc(docRef, {
      status: 'pending_signature',
      sentAt: Timestamp.now(),
      sentTo: beneficiary.email,
      emailSent: true
    });

    return {
      success: true,
      message: 'Contrat de mandat envoyé par email avec succès',
      emailResult: result.data
    };
  } catch (error) {
    console.error('Erreur lors de l\'envoi du contrat de mandat:', error);
    throw error;
  }
}

/**
 * Traite la signature du contrat de mandat
 */
export async function traiterSignatureMandat(db, beneficiaryId, documentId, signatureData, ipAddress) {
  try {
    const { signatureImage, signedBy } = signatureData;

    // Ajouter la signature au document
    await addSignature(db, beneficiaryId, documentId, signatureImage, signedBy, ipAddress);

    // Mettre à jour le statut du bénéficiaire pour indiquer que le mandat est signé
    const beneficiaryRef = doc(db, 'clients', beneficiaryId);
    await updateDoc(beneficiaryRef, {
      mandateSigne: true,
      mandateSigneAt: Timestamp.now()
    });

    return {
      success: true,
      message: 'Signature enregistrée avec succès'
    };
  } catch (error) {
    console.error('Erreur lors du traitement de la signature:', error);
    throw error;
  }
}

/**
 * Récupère le statut du contrat de mandat pour un bénéficiaire
 */
export async function obtenirStatutMandat(db, beneficiaryId) {
  try {
    const { existe, mandat } = await verifierMandatExistant(db, beneficiaryId);

    if (!existe) {
      return {
        status: 'non_genere',
        message: 'Aucun contrat de mandat généré'
      };
    }

    return {
      status: mandat.status,
      documentId: mandat.id,
      createdAt: mandat.createdAt,
      sentAt: mandat.sentAt,
      signedAt: mandat.signature?.signedAt,
      message: getStatusMessage(mandat.status)
    };
  } catch (error) {
    console.error('Erreur lors de la récupération du statut du mandat:', error);
    throw error;
  }
}

/**
 * Génère un message descriptif pour chaque statut
 */
function getStatusMessage(status) {
  const messages = {
    'draft': 'Contrat créé, en attente d\'envoi',
    'pending_signature': 'Contrat envoyé, en attente de signature',
    'signed': 'Contrat signé',
    'archived': 'Contrat archivé',
    'non_genere': 'Aucun contrat généré'
  };

  return messages[status] || 'Statut inconnu';
}

/**
 * Génère les contrats de mandat pour tous les bénéficiaires actifs sans mandat
 */
export async function genererMandatsEnMasse(db, userId) {
  try {
    // Récupérer tous les bénéficiaires actifs
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('status', '==', 'ACTIVE'));
    const snapshot = await getDocs(q);

    const resultats = {
      total: 0,
      generes: 0,
      erreurs: 0,
      deja_existants: 0,
      details: []
    };

    for (const docSnap of snapshot.docs) {
      const beneficiaryId = docSnap.id;
      const beneficiary = docSnap.data();
      resultats.total++;

      try {
        // Vérifier si un mandat existe déjà
        const { existe } = await verifierMandatExistant(db, beneficiaryId);

        if (existe) {
          resultats.deja_existants++;
          resultats.details.push({
            beneficiaryId,
            nom: `${beneficiary.firstName} ${beneficiary.lastName}`,
            status: 'deja_existant'
          });
          continue;
        }

        // Créer le contrat de mandat
        await creerContratMandat(db, beneficiaryId, userId);
        resultats.generes++;
        resultats.details.push({
          beneficiaryId,
          nom: `${beneficiary.firstName} ${beneficiary.lastName}`,
          status: 'genere'
        });
      } catch (error) {
        resultats.erreurs++;
        resultats.details.push({
          beneficiaryId,
          nom: `${beneficiary.firstName} ${beneficiary.lastName}`,
          status: 'erreur',
          erreur: error.message
        });
      }
    }

    return resultats;
  } catch (error) {
    console.error('Erreur lors de la génération en masse:', error);
    throw error;
  }
}

/**
 * Télécharge le PDF du contrat de mandat
 */
export async function telechargerPDFMandat(db, beneficiaryId) {
  try {
    const pdfBlob = await genererPDFMandat(db, beneficiaryId);

    // Récupérer les données du bénéficiaire pour le nom du fichier
    const beneficiaryRef = doc(db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);
    const beneficiary = beneficiarySnap.data();

    const filename = `Contrat_Mandat_${beneficiary.lastName}_${beneficiary.firstName}.pdf`;

    // Créer un lien de téléchargement
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return {
      success: true,
      message: 'PDF téléchargé avec succès'
    };
  } catch (error) {
    console.error('Erreur lors du téléchargement du PDF:', error);
    throw error;
  }
}

export default {
  genererHTMLMandat,
  genererPDFMandat,
  verifierMandatExistant,
  creerContratMandat,
  envoyerContratMandat,
  traiterSignatureMandat,
  obtenirStatutMandat,
  genererMandatsEnMasse,
  telechargerPDFMandat
};
